import { isGranted } from '@utilities-one/gpm-ui';

import type {
  AccessUser,
  WithoutPermission,
  WithoutRestriction,
  WithPermission,
  WithRestriction,
} from '@/entities/user/types';
import { mergePermissions } from '@/entities/user/utils/mergePermissions';

/**
 * Permission checks against the user's EFFECTIVE bag — the role's
 * `permissions` merged with the personal override (ignored on a strict role),
 * see `mergePermissions` (UG-5193). Same rule as the API's `hasPermission`.
 *
 * `hasPermission(user, a, b)` is "any of": true when at least one dot-path is
 * granted. A leaf must be TRUTHY (a `false` override denies); a group path
 * (e.g. `'jobs.view'`) passes when any permission under it is granted.
 */
export const hasPermission = (user: AccessUser, ...permissions: string[]): boolean => {
  const bag = mergePermissions(user) ?? {};
  return permissions.some((permission) => isGranted(bag, permission));
};

/**
 * Tolerant single-rule check: `undefined` means "no permission required".
 * Accepts the same `string | string[]` shape as `WithPermission.permission`.
 */
export const hasAccess = (user: AccessUser, permission?: string | string[]): boolean => {
  if (permission === undefined) return true;
  return hasPermission(user, ...(Array.isArray(permission) ? permission : [permission]));
};

/**
 * Drop items whose `permission` the user lacks, stripping the `permission` key
 * from survivors. Recurses into `items` (nav-menu style nesting).
 */
export const filterByPermissions = <T>(user: AccessUser, list: WithPermission<T>[]): WithoutPermission<T>[] => {
  return list
    .filter(({ permission }) => hasAccess(user, permission))
    .map(({ permission: _permission, ...item }) => {
      const newItem = { ...item } as WithoutPermission<T> & { items?: WithPermission<unknown>[] };
      if (Array.isArray(newItem.items)) {
        newItem.items = filterByPermissions(user, newItem.items) as WithPermission<unknown>[];
      }
      return newItem as WithoutPermission<T>;
    });
};

/**
 * Drop items whose `restriction(user)` returns false, stripping the
 * `restriction` key from survivors. Recurses into `items`; a nested list that
 * filters to empty removes the `items` key entirely (legacy behavior).
 */
export const filterByRestrictions = <T>(user: AccessUser, list: WithRestriction<T>[]): WithoutRestriction<T>[] => {
  return list
    .filter(({ restriction }) => !restriction || restriction(user))
    .map(({ restriction: _restriction, ...item }) => {
      const newItem = { ...item } as WithoutRestriction<T> & { items?: WithRestriction<unknown>[] };
      if (Array.isArray(newItem.items)) {
        const filteredChildren = filterByRestrictions(user, newItem.items);
        if (filteredChildren.length > 0) {
          newItem.items = filteredChildren as WithRestriction<unknown>[];
        } else {
          delete newItem.items;
        }
      }
      return newItem as WithoutRestriction<T>;
    });
};
